import React, { useEffect, useState } from 'react';
import { Box } from '@saviynt/design-system';

import { useChatOverlayContext } from '../../../context/chatOverlayContext';
import {
  type AskApiResponse,
  type SampleDataAPIResponse,
} from '../../../data-layer/types';
import useDashboardApi from '../../../hooks/use-dashboardapi';
import { Logger } from '../../../services';
import { useInvertedLocalize } from '../../../utils/localization';
import { invertedMessages } from '../../../utils/messages';
import DataSectionHeader from '../panel-headers/DataSectionHeader/DataSectionHeader';
import TopHeaderPanel from '../panel-headers/TopHeaderPanel/TopHeaderPanel';
import {
  GeneratedContentTypeEnum,
  TOGGLE_VALUES,
  type ToggleSelectionType,
} from '../types';

import ChartSql from './ChartSql/ChartSql';
import ChartVisualization from './ChartVisualization/ChartVisualization';

import './ChartPanel.css';

interface ChartPanelProps {
  data: AskApiResponse | SampleDataAPIResponse;
}

export default function ChartPanel({ data }: ChartPanelProps) {
  const { updateChatOverlayState } = useChatOverlayContext();
  const l = useInvertedLocalize(invertedMessages);
  const [toggleSelection, setToggleSelection] = useState<ToggleSelectionType>(
    TOGGLE_VALUES.VISUALIZATION
  );

  const { fetchDashboards } = useDashboardApi();
  const { chatOverlayState } = useChatOverlayContext();

  useEffect(() => {
    if (chatOverlayState.dashboardLists.length > 0) {
      return;
    }

    fetchDashboards();
  }, [chatOverlayState.dashboardLists.length, fetchDashboards]);

  if (
    !('content' in data) ||
    data.contentType !== 'chart' ||
    !Array.isArray(data.content)
  ) {
    updateChatOverlayState('showGeneratedContent', false);

    Logger.info(
      'ChartPanel --> panel kind - chart  --> no content present in response',
      { data }
    );

    return null;
  }

  return (
    <section className='Ispm-ChartPanel'>
      <TopHeaderPanel
        headerText={data.request?.freeText ?? l('New Chart')}
        toggleSelection={toggleSelection}
        panelType={GeneratedContentTypeEnum.CHART}
      />
      <Box className='Ispm-ChartPanel-headerContentContainer'>
        <DataSectionHeader
          toggleSelection={toggleSelection}
          setToggleSelection={setToggleSelection}
        />
        {toggleSelection === TOGGLE_VALUES.VISUALIZATION ? (
          <ChartVisualization data={data} />
        ) : (
          <ChartSql query={data.query} />
        )}
      </Box>
    </section>
  );
}
